import { Injectable, inject } from "@angular/core";
import { AlertService } from "@ui";
import { BehaviorSubject, type Observable } from "rxjs";
import { tap } from "rxjs/operators";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import { type StatusResponse, TaskType } from "@pienso/ingest-types/task.interface";
import type { TRANSFORM_TYPE, Transform, TransformWithCorpus } from "@pienso/ingest-types/transform.interface";
import { CorpusDataService } from "../services/corpus-data.service";
import { TaskDataService } from "../services/task-data.service";
import type { Transformer } from "./transformer.interface";
import { TransformerRegistryService } from "./transformer-registry.service";

@Injectable({ providedIn: "root" })
export class TransformerService {
  private registry = inject(TransformerRegistryService);
  private corpusService = inject(CorpusDataService);
  private taskService = inject(TaskDataService);
  private alertService = inject(AlertService);

  private _activeTransformer$ = new BehaviorSubject<Transformer | null>(null);
  public get activeTransformer$(): Observable<Transformer | null> {
    return this._activeTransformer$.asObservable();
  }
  public get activeTransformer() {
    return this._activeTransformer$.getValue();
  }

  public watchCorpusTransforms(corpusId: number): Observable<StatusResponse[]> {
    return this.taskService.watchEntityTasks(corpusId, [
      TaskType.TRANSFORM_CREATE,
      TaskType.TRANSFORM_UPDATE,
      TaskType.TRANSFORM_REMOVE,
    ]);
  }

  /** Initiate a new transformation tool on the given corpus */
  transformCorpus(corpus: Corpus, transformerId: string) {
    const transformer = this.registry.transformerForId(transformerId);
    if (!transformer) {
      throw new Error(`Transformer with ID ${transformerId} not found`);
    }
    const promise: Promise<TransformWithCorpus> = transformer.init(corpus);
    this._activeTransformer$.next(transformer);
    promise
      .then((xform) => {})
      .catch((reason) => {
        if (reason) {
          this.alertService.error(reason);
        }
      });
  }

  editTransform(transform: TransformWithCorpus) {
    const xformer = this.registry.transformerForType(transform.transformType as TRANSFORM_TYPE);
    const promise: Promise<TransformWithCorpus> = xformer.edit(transform);
    this._activeTransformer$.next(xformer);
    promise
      .then((xform) => {})
      .catch((reason) => {
        if (reason) {
          this.alertService.error(reason);
        }
      });
  }

  deleteTransform(transform: Transform) {
    return this.corpusService
      .deleteTransform(transform.corpusId, transform.id)
      .pipe(tap(() => this._activeTransformer$.next(null)));
  }

  cancel() {
    this.activeTransformer?.close();
    this._activeTransformer$.next(null);
  }

  reset() {
    this._activeTransformer$.next(null);
  }
}
